import React, { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const reasons = [
  "Deep local knowledge of Rwanda’s infrastructure landscape.",
  "End-to-end engineering delivery with quality governance.",
  "Sustainable and resilient design principles in every phase.",
  "Transparent project controls with measurable outcomes.",
];

const AboutPremium = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      ScrollTrigger.matchMedia({
        "(min-width: 768px)": () => {
          const items = gsap.utils.toArray(".why-item");
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top top+=90",
              end: "+=140%",
              scrub: true,
              pin: true,
            },
          });

          tl.from(".about-left", { x: -80, opacity: 0, duration: 1 })
            .from(".about-right", { x: 80, opacity: 0, duration: 1 }, "<0.05")
            .from(
              items,
              {
                x: (i) => (i % 2 === 0 ? -80 : 80),
                opacity: 0,
                stagger: 0.2,
                duration: 0.8,
              },
              "-=0.5"
            );
        },
        "(max-width: 767px)": () => {
          gsap.from(".about-col", {
            y: 40,
            opacity: 0,
            stagger: 0.15,
            duration: 0.7,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 78%",
            },
          });
        },
      });
    },
    { scope: sectionRef }
  );

  return (
    <section id="about" ref={sectionRef} className="premium-section-bg relative overflow-hidden py-24 sm:py-28">
      <div className="premium-glow decor-drift pointer-events-none absolute inset-0 opacity-70" aria-hidden="true">
        <div className="decor-pulse absolute -left-10 top-8 h-40 w-40 rounded-full bg-gradient-to-br from-sky-400/20 via-blue-400/10 to-transparent blur-2xl" />
        <div className="decor-float absolute right-8 top-10 text-[120px] font-black leading-none text-transparent [background:linear-gradient(135deg,rgba(56,189,248,0.28),rgba(29,78,216,0.08))] bg-clip-text">
          01
        </div>
        <div className="decor-float absolute bottom-6 left-10 text-[96px] font-black leading-none text-transparent [background:linear-gradient(135deg,rgba(14,165,233,0.22),rgba(37,99,235,0.06))] bg-clip-text [animation-delay:1.2s]">
          02
        </div>
      </div>
      <div className="section-shell relative z-10">
        <div className="section-title">
          <p className="section-kicker">About B-fos</p>
          <h2 className="section-heading">Engineering confidence from concept to delivery</h2>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <div className="about-col about-left premium-card p-8">
            <p className="text-sm leading-relaxed text-slate-100 sm:text-base">
              B-fos is a Kigali-based civil engineering and consultancy practice dedicated
              to shaping Rwanda’s next generation of urban and infrastructure systems. We
              combine technical precision, stakeholder collaboration, and forward-thinking
              planning to create projects that remain valuable for decades.
            </p>
          </div>
          <div className="about-col about-right premium-card p-8">
            <h3 className="text-xl font-semibold text-white">Why Choose Us</h3>
            <ul className="mt-6 space-y-3">
              {reasons.map((reason, idx) => (
                <li
                  key={reason}
                  className="why-item rounded-xl border border-white/25 bg-white/10 px-4 py-3 text-sm text-slate-100"
                >
                  <span className="mr-2 text-amber-400">0{idx + 1}.</span>
                  {reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPremium;
